import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { MaskedNumberInput } from "@/components/ui/masked-input";
import type { PointConversionPartner } from "../../../../domain/model/RewardCatalogModel";
import { FetchPointConversionPartnersUseCase } from "../../../../domain/usecase";

export interface PointConversionFieldsSectionProps {
	readonly pointProvider: string;
	readonly pointProviderError?: string;
	readonly amount: string;
	readonly amountError?: string;
	readonly startDate: string;
	readonly startDateError?: string;
	readonly endDate: string;
	readonly endDateError?: string;
	readonly onChangePointProvider: (value: string) => void;
	readonly onChangeAmount: (value: string) => void;
	readonly onChangeStartDate: (value: string) => void;
	readonly onChangeEndDate: (value: string) => void;
}

export const PointConversionFieldsSection = ({
	pointProvider,
	pointProviderError,
	amount,
	amountError,
	startDate,
	startDateError,
	endDate,
	endDateError,
	onChangePointProvider,
	onChangeAmount,
	onChangeStartDate,
	onChangeEndDate,
}: PointConversionFieldsSectionProps): React.JSX.Element => {
	const [partners, setPartners] = useState<readonly PointConversionPartner[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [fetchError, setFetchError] = useState<string | null>(null);
	const [selectedPartner, setSelectedPartner] = useState<PointConversionPartner | null>(null);

	useEffect(() => {
		const fetchPartners = async () => {
			try {
				setIsLoading(true);
				setFetchError(null);
				const data = await FetchPointConversionPartnersUseCase();
				setPartners(data);
			} catch (error) {
				console.error("Failed to fetch Point Conversion partners:", error);
				setFetchError("Failed to load Point Conversion partners");
			} finally {
				setIsLoading(false);
			}
		};

		fetchPartners();
	}, []);

	// Update selected partner when pointProvider changes
	useEffect(() => {
		const partner = partners.find((p) => p.partnerId === pointProvider);
		setSelectedPartner(partner ?? null);
	}, [pointProvider, partners]);

	return (
		<>
			<div className="grid grid-cols-2 gap-4">
				<div className="flex flex-col gap-1.5">
					<label htmlFor="pointProvider" className="text-sm font-semibold text-gray-800 text-left">
						Point Provider
					</label>
					<Select
						id="pointProvider"
						value={pointProvider}
						onChange={(event) => onChangePointProvider(event.target.value)}
						errorMessage={pointProviderError ?? fetchError ?? undefined}
						disabled={isLoading}
					>
						<option value="" disabled>
							{isLoading ? "Loading..." : "Select Point Provider"}
						</option>
						{partners.map((partner) => (
							<option key={partner.partnerId} value={partner.partnerId}>
								{partner.partnerName}
							</option>
						))}
					</Select>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="amount" className="text-sm font-semibold text-gray-800 text-left">
						Amount
					</label>
					<div className="relative flex items-center w-full h-[42px] px-3 rounded-lg border border-gray-200 bg-white focus-within:border-[#001a41] focus-within:shadow-[0_0_0_1px_rgba(0,26,65,0.08)] transition-all duration-150">
						<span className="text-sm font-semibold text-gray-800 whitespace-nowrap mr-2">1 Point =</span>
						<MaskedNumberInput
							id="amount"
							value={amount}
							onChange={onChangeAmount}
							placeholder="Amount"
							errorMessage={undefined}
							className="border-0 px-0 shadow-none focus:border-0 focus:shadow-none h-auto py-0"
						/>
						<span className="text-sm font-semibold text-gray-800 whitespace-nowrap ml-auto">
							{selectedPartner?.partnerName ?? "Partner Point"}
						</span>
					</div>
					{amountError && (
						<p className="text-xs text-red-600">{amountError}</p>
					)}
				</div>
			</div>

			<div className="grid grid-cols-2 gap-4">
				<div className="flex flex-col gap-1.5">
					<label htmlFor="startDate" className="text-sm font-semibold text-gray-800 text-left">
						Start Date
					</label>
					<Input
						id="startDate"
						type="date"
						value={startDate}
						onChange={(event) => onChangeStartDate(event.target.value)}
						errorMessage={startDateError}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="endDate" className="text-sm font-semibold text-gray-800 text-left">
						End Date
					</label>
					<Input
						id="endDate"
						type="date"
						value={endDate}
						onChange={(event) => onChangeEndDate(event.target.value)}
						errorMessage={endDateError}
					/>
				</div>
			</div>
		</>
	);
};

